import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getBreadcrumbSchema } from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { categories, allVideos } from '@/mocks/videos';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'newest'>('rating');
  const [activeTag, setActiveTag] = useState<string>('全部');

  const categoryVideos = useMemo(() => {
    return allVideos.filter((v) => v.category === currentCategory.id);
  }, [currentCategory.id]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    categoryVideos.forEach((v) => v.tags.forEach((t) => tagSet.add(t)));
    return ['全部', ...Array.from(tagSet)];
  }, [categoryVideos]);

  const filteredVideos = useMemo(() => {
    let result = activeTag === '全部' ? categoryVideos : categoryVideos.filter((v) => v.tags.includes(activeTag));
    if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'views') {
      result = [...result].sort((a, b) => {
        const aNum = parseFloat(a.views.replace('万', ''));
        const bNum = parseFloat(b.views.replace('万', ''));
        return bNum - aNum;
      });
    } else {
      result = [...result].sort((a, b) => b.updateDate.localeCompare(a.updateDate));
    }
    return result;
  }, [categoryVideos, activeTag, sortBy]);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: currentCategory.name },
  ];

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '神马视频',
        url: 'https://www.shenmashipin.com',
      },
      {
        '@type': 'WebSite',
        name: '神马视频',
        url: 'https://www.shenmashipin.com',
      },
      {
        '@type': 'CollectionPage',
        name: `${currentCategory.name} - 神马视频`,
        description: currentCategory.description,
        url: `https://www.shenmashipin.com/category/${slug}`,
        isPartOf: {
          '@type': 'WebSite',
          name: '神马视频',
        },
      },
      getBreadcrumbSchema(breadcrumbItems),
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${currentCategory.name} - 神马视频 | ${currentCategory.keywords}`}
        description={currentCategory.description}
        keywords={currentCategory.keywords}
        canonicalUrl={`https://www.shenmashipin.com/category/${slug}`}
        schema={pageSchema}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb />

          <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100">
            <div className="max-w-[1200px] mx-auto">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className={`${currentCategory.icon} text-xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {currentCategory.name}
                  </h1>
                  <p className="text-sm text-foreground-500 mt-1">
                    {currentCategory.description}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mt-6">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                      cat.id === currentCategory.id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-6 md:py-8 bg-background-100 border-b border-background-200">
            <div className="max-w-[1200px] mx-auto">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-2">
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setActiveTag(tag)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        activeTag === tag
                          ? 'bg-accent-100 text-accent-700'
                          : 'bg-background-50 text-foreground-500 hover:bg-background-200'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
                  {[
                    { key: 'rating' as const, label: '评分最高' },
                    { key: 'views' as const, label: '播放最多' },
                    { key: 'newest' as const, label: '最新上架' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setSortBy(opt.key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        sortBy === opt.key
                          ? 'bg-secondary-100 text-secondary-700'
                          : 'bg-background-50 text-foreground-500 hover:bg-background-200'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-8 md:py-10 bg-background-50">
            <div className="max-w-[1200px] mx-auto">
              {filteredVideos.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
                    <i className="ri-inbox-line text-3xl"></i>
                  </div>
                  <p className="text-foreground-500">暂无相关内容</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {filteredVideos.map((video) => (
                    <VideoCard key={video.id} videoId={video.id} size="normal" />
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
        <Footer />
      </div>
    </>
  );
}